import { Typography } from "@material-tailwind/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { agentSessionLogout } from "../../api/agents/agentsApi";
import { JOIN_WORKSPACE, USER_CHECK } from "../../api/apiRoutes";
import AXIOS_INSTANCE from "../../api/axiosInstance";
import { getDataINSTANCE } from "../../api/controller";
import {
  getWorkspaceByUserId,
  getWorkspaceForumStatus,
} from "../../api/workspace/workspace";
import LogoType from "../../Assets/Svg/Logotype.svg";
import LoadingSpinner from "../../Components/UI/LoadingSpinner";
import { usePlan } from "../../Context/PlanContext";
import { useSocket } from "../../Context/SocketContext";
import {
  useDebatesStatusStore,
  useSubworkspaceStore,
  useUserStore,
  useWorkspaceStore,
} from "../../Context/store";
import { ADD_WORKSPACES, primaryColor } from "../../Utils/Constant";
import { getUTCDate } from "../../Utils/Helper";
import { useSubWorkspacesByWorkspaceId } from "../../Web-Hooks/Workspace/use-subworkspace";
import { useSignalR } from "../../hooks/useSignalR";

const Join = () => {
  const navigate = useNavigate();
  const { user, logout } = useUserStore();
  const { selectWorkspace } = useWorkspaceStore();
  const { disconnect } = useSocket();
  const { sendMessage, connection } = useSignalR();
  const { selectSubWorkspace, setSubWorkspaceList } = useSubworkspaceStore();
  const { setAllForumStatus, setCurrentForumStatus } = useDebatesStatusStore();
  const location = useLocation();
  const { search } = location;
  const [loader, setLoader] = useState(true);
  const filterDate = search?.split("&")[3]?.split("=")[1];
  const Email = search?.split("&")[2]?.split("=")[1];
  const WorkspaceId = search?.split("&")[0]?.split("=")[1];
  const SubWorkspaceId = search?.split("&")[1]?.split("=")[1];
  const loggedInUser = localStorage.getItem("user")
    ? JSON.parse(localStorage.getItem("user"))
    : user;
  const { currentPlan } = usePlan();
  const queryClient = useQueryClient();
  const { refetch } = useSubWorkspacesByWorkspaceId(
    WorkspaceId,
    loggedInUser?.userId
  );

  const joinUser = async (body) => {
    try {
      const {
        data: { message: data },
      } = await AXIOS_INSTANCE.post(JOIN_WORKSPACE, body);
      return data;
    } catch (error) {
      navigate("/invitation-expired", { replace: true });
      throw new Error("Failed to join user");
    }
  };

  const { mutateAsync: handleJoin } = useMutation({
    mutationFn: async (body) => {
      return await joinUser(body);
    },
    onSuccess: () => {
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: ["workspaces"],
        }),
        queryClient.invalidateQueries({
          queryKey: ["sub-workspaces"],
        }),
      ]);
    },
  });

  useEffect(() => {
    if (filterDate && connection) {
      const joinDate = new Date(filterDate);
      const joinDateFormat = new Date().toISOString();
      const currentDate = new Date(joinDateFormat);
      const dateDifference = currentDate - joinDate; // difference in hours
      const timeDifference = dateDifference / (1000 * 60 * 60);
      // difference in hours
      if (timeDifference > 48) {
        navigate("/invitation-expired", { replace: true });
      } else {
        handleProceed();
      }
    } // eslint-disable-next-line
  }, [filterDate, connection]);

  const handleUpgradePlan = async ({ title, message }, cf) => {
    return Swal.fire({
      imageUrl: LogoType,
      imageWidth: 250,
      imageHeight: 150,
      imageAlt: "Custom image",
      width: 700,
      color: primaryColor,
      customClass: {
        title: "p-0 !my-2",
        image: "p-0 m-0 w-full mx-auto",
        popup: "flex flex-col items-center gap-3 ",
        htmlContainer: " !py-0 !px-16 !my-2",
        actions: "m-0 my-2",
        container: "!z-[99999]",
      },
      titleText: title,
      text: message,
      confirmButtonText: "Upgrade",
      showCancelButton: true,
      confirmButtonAriaLabel: "Upgrade",
      confirmButtonColor: primaryColor,
      cancelButtonAriaLabel: "cancel",
      cancelButtonText: "Cancel",
      stopKeydownPropagation: true,
      showClass: {
        popup: `
            animate__animated
            animate__fadeInUp
            animate__faster
          `,
      },
      hideClass: {
        popup: `
            animate__animated
            animate__fadeOutDown
            animate__faster
          `,
      },
    }).then((result) => {
      if (result.isConfirmed) {
        cf();
      }
      if (result.isDismissed) {
        navigate(-1);
      }
    });
  };

  const handleProceed = async () => {
    setLoader(true);

    try {
      // Check if the user already exists in the system
      const data = await checkUser();

      if (!data?.isUserExist) {
        // If the user does not exist, redirect the user to the signup page
        navigate(
          `/signup?Email=${Email}&WorkspaceId=${WorkspaceId}&SubWorkspaceId=${SubWorkspaceId}`,
          { state: { Email, WorkspaceId, SubWorkspaceId } }
        );
        return;
      }

      if (Email !== loggedInUser?.email) {
        // If the user is not the same as the logged in user, log the user out and redirect the user to the login
        agentSessionLogout({
          user_id: user?.userId,
          session_id: user?.sessionId,
        });
        disconnect();
        logout();
        navigate(
          `/?Email=${Email}&WorkspaceId=${WorkspaceId}&SubWorkspaceId=${SubWorkspaceId}`,
          { replace: true }
        );
        return;
      }

      // Check if the user has already joined the workspace
      if (currentPlan?.maxWorkSpaceJoinCount !== "unlimited") {
        // If the user has not joined the workspace, check if the user has reached the maximum number of workspaces
        const workspace = await getWorkspaceByUserId(loggedInUser?.userId);
        const joinedWorkspace = workspace?.listWorkspaces?.filter(
          (workspace) => workspace?.createdBy?.userId !== loggedInUser?.userId
        );
        const alreadyPartOfWorkspace = joinedWorkspace?.find(
          (ws) => ws?.workSpaceId === WorkspaceId
        );
        if (!alreadyPartOfWorkspace) {
          if (joinedWorkspace?.length >= currentPlan?.maxWorkSpaceJoinCount) {
            // If the user has reached the maximum number of workspaces, show the upgrade plan popup
            handleUpgradePlan(ADD_WORKSPACES, () => {
              navigate("/upgrade-plan?plan=base-plan", { replace: true });
            });
            return;
          }
        }
      }

      // If the user has not joined the workspace, call the join API
      await handleJoin({
        Email: Email,
        Workspaces: [
          {
            WorkspaceId: WorkspaceId,
            SubworkspaceIds: [SubWorkspaceId],
          },
        ],
        CreatedAt: getUTCDate(),
      });

      // Get the list of workspaces for the user
      const workspace = await getWorkspaceByUserId(user?.userId);
      const findOne = workspace?.listWorkspaces.find(
        (workspace) => workspace.workSpaceId === WorkspaceId
      );

      // Get the list of subworkspaces for the workspace
      const { data: subWorkSpaces } = await refetch();
      setSubWorkspaceList(subWorkSpaces);
      const subFindOne = subWorkSpaces.find(
        (subworkspace) => subworkspace.subWorkSpaceId === SubWorkspaceId
      );

      if (findOne && subFindOne) {
        await sendMessage("JoinWorkspaceGroup", findOne.workSpaceId);
        // If the workspace and subworkspace exist, select the workspace and subworkspace
        selectWorkspace(findOne);
        selectSubWorkspace(subFindOne);
        const response = await getWorkspaceForumStatus({
          userId: user?.userId,
          workspaceId: findOne.workSpaceId,
        });

        if (response?.data) {
          setAllForumStatus(response.data);
          const forumStatus = response.data?.forums?.[subFindOne?.subWorkSpaceId];
          setCurrentForumStatus(forumStatus);
        }

        navigate(`/feed/${subFindOne?.subWorkSpaceId}`, { replace: true });
      } else {
        // If the workspace or subworkspace does not exist, redirect the user to the create workspace page
        navigate("/createworkspace", {
          state: {
            userId: user?.userId,
            workspaceId: WorkspaceId,
            subworkspaceId: SubWorkspaceId,
          },
        });
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoader(false);
    }
  };

  const checkUser = async () => {
    const data = await getDataINSTANCE(
      `${USER_CHECK}?Email=${Email}&WorkSpaceId=${WorkspaceId}&SubWorkspaceId=${SubWorkspaceId}`,
      "Failed to fetch Sub Workspaces Users"
    );
    return data;
  };

  return (
    <section className="h-screen flex flex-col md:flex-row items-center justify-between p-8">
      <div className="flex flex-col justify-center items-center gap-4  w-full">
        <Typography variant="h1">Hello, Welcome to Debatebase</Typography>

        {loader ? <LoadingSpinner /> : null}
      </div>
    </section>
  );
};

export default Join;
